import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Mock用户数据
const MOCK_USERS = [
  {
    id: 'OP-20260001',
    name: '张三',
    role: 'operator',
    department: '运营部',
    avatar: '👤',
    phone: '13800138000',
    email: 'zhangsan@deeptrust.com',
    position: '运营专员',
    joinDate: '2026-01-15',
    bio: '负责平台日常运营和用户管理',
    status: 'active'
  },

  {
    id: 'ADMIN-20260003',
    name: '管理员',
    role: 'admin',
    department: '平台管理部',
    avatar: '👑',
    phone: '13800138002',
    email: 'admin@deeptrust.com',
    position: '平台管理员',
    joinDate: '2026-01-01',
    bio: '拥有最高权限，负责平台整体管理',
    status: 'active'
  }
]

// Mock权限数据
const MOCK_PERMISSIONS = {
  operator: {
    platform: ['*:platform:*:view', '*:platform:profile:view', '*:platform:messages:view', '*:platform:audit:view'],
    business_platforms: ['*:platform_business:*:view'],
    internal_platforms: ['*:platform_toolbox:*:*', '*:platform_workflow:*:*', '*:platform_agent:*:*']
  },

  admin: {
    platform: ['*:platform:*:*'],
    business_platforms: ['*:platform_*:*:*'],
    internal_platforms: ['*:platform_*:*:*']
  }
}

// Mock消息数据
const MOCK_MESSAGES = [
  {
    id: 1,
    category: 'system',
    title: '系统维护通知',
    content: '平台将于本周五凌晨2:00-4:00进行系统维护，期间可能会影响部分功能使用。',
    senderName: '系统管理员',
    senderRole: '管理员',
    senderDepartment: '技术部',
    senderAvatar: '👨‍💻',
    time: '2026-03-28T09:30:00',
    read: false,
    priority: 'high',
    urgent: true,
    tags: ['平台相关', '重要']
  },
  {
    id: 2,
    category: 'todo',
    title: '新的待办任务：审核用户申请',
    content: '您有5个新的用户申请需要审核，请尽快处理。',
    senderName: '任务系统',
    senderRole: '系统',
    senderDepartment: '运营部',
    senderAvatar: '📋',
    time: '2026-03-28T10:15:00',
    read: false,
    priority: 'medium',
    urgent: false,
    tags: ['待处理', '需回复']
  }
]

// Mock审计日志
const MOCK_AUDIT_LOGS = [
  {
    id: 1,
    operationType: 'login',
    operationContent: '用户登录总平台',
    userName: '张三',
    userRole: 'operator',
    userAvatar: '👤',
    timestamp: '2026-03-28T10:30:00',
    result: 'success',
    riskLevel: 'low'
  },
  {
    id: 2,
    operationType: 'permission',
    operationContent: '修改用户权限设置',
    userName: '管理员',
    userRole: 'admin',
    userAvatar: '👑',
    timestamp: '2026-03-28T10:15:00',
    result: 'success',
    riskLevel: 'high'
  }
]

export const usePlatformStore = defineStore('platform', () => {
  // 当前用户
  const currentUser = ref<any>(null)
  
  // 用户列表
  const users = ref([...MOCK_USERS])
  
  // 消息列表
  const messages = ref([...MOCK_MESSAGES])
  
  // 审计日志
  const auditLogs = ref([...MOCK_AUDIT_LOGS])
  
  // 登录状态
  const isLoggedIn = computed(() => !!currentUser.value)
  
  // 当前用户权限
  const currentUserPermissions = computed(() => {
    if (!currentUser.value) return []
    // 将technician角色映射到operator权限
    const role = currentUser.value.role === 'technician' ? 'operator' : currentUser.value.role
    return MOCK_PERMISSIONS[role as keyof typeof MOCK_PERMISSIONS] || {}
  })
  
  // 未读消息数量
  const unreadMessageCount = computed(() => {
    return messages.value.filter(msg => !msg.read).length
  })
  
  // 高风险操作数量
  const highRiskOperationCount = computed(() => {
    return auditLogs.value.filter(log => log.riskLevel === 'high').length
  })
  
  // 设置当前用户
  const setUser = (user: any) => {
    // 将technician角色转换为operator以符合V7.2标准
    if (user.role === 'technician') {
      user = { ...user, role: 'operator' }
    }
    currentUser.value = user
    // 保存到本地存储
    localStorage.setItem('platform_user', JSON.stringify(user))
  }
  
  // 从本地存储加载用户
  const loadUserFromStorage = () => {
    const storedUser = localStorage.getItem('platform_user')
    if (storedUser) {
      const user = JSON.parse(storedUser)
      // 将technician角色转换为operator以符合V7.2标准
      if (user.role === 'technician') {
        user.role = 'operator'
        // 更新本地存储
        localStorage.setItem('platform_user', JSON.stringify(user))
      }
      currentUser.value = user
    }
  }
  
  // 登录
  const login = (username: string, password: string): boolean => {
    const user = MOCK_USERS.find(u => 
      (u.name === username || u.email === username) && 
      password === `${username}123`
    )
    
    if (user) {
      setUser(user)
      // 记录登录日志
      auditLogs.value.unshift({
        id: Date.now(),
        operationType: 'login',
        operationContent: '用户登录总平台',
        userName: user.name,
        userRole: user.role,
        userAvatar: user.avatar,
        timestamp: new Date().toISOString(),
        result: 'success',
        riskLevel: 'low'
      })
      return true
    }
    
    // 记录失败的登录尝试
    auditLogs.value.unshift({
      id: Date.now(),
      operationType: 'login',
      operationContent: '用户登录尝试失败',
      userName: '未知用户',
      userRole: 'unknown',
      userAvatar: '❓',
      timestamp: new Date().toISOString(),
      result: 'failed',
      riskLevel: 'high'
    })
    
    return false
  }
  
  // 登出
  const logout = () => {
    currentUser.value = null
    localStorage.removeItem('platform_user')
    localStorage.removeItem('platform_token')
  }
  
  // 添加用户
  const addUser = (userData: any) => {
    const newUser = {
      id: `USER-${Date.now()}`,
      ...userData,
      status: 'active',
      avatar: userData.avatar || '👤'
    }
    users.value.push(newUser)
    return newUser
  }
  
  // 更新用户
  const updateUser = (userId: string, userData: any) => {
    const index = users.value.findIndex(u => u.id === userId)
    if (index > -1) {
      users.value[index] = { ...users.value[index], ...userData }
    }
  }
  
  // 删除用户
  const deleteUser = (userId: string) => {
    const index = users.value.findIndex(u => u.id === userId)
    if (index > -1) {
      users.value.splice(index, 1)
    }
  }
  
  // 标记消息为已读
  const markMessageAsRead = (messageId: number) => {
    const message = messages.value.find(msg => msg.id === messageId)
    if (message && !message.read) {
      message.read = true
    }
  }
  
  // 标记所有消息为已读
  const markAllMessagesAsRead = () => {
    messages.value.forEach(msg => {
      msg.read = true
    })
  }
  
  // 添加审计日志
  const addAuditLog = (logData: any) => {
    const newLog = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...logData
    }
    auditLogs.value.unshift(newLog)
    return newLog
  }
  
  // 检查权限
  const hasPermission = (permission: string): boolean => {
    if (!currentUser.value) return false
    
    const userPermissions = currentUserPermissions.value
    const allPermissions = Object.values(userPermissions).flat()
    
    // 简单的权限检查逻辑
    return allPermissions.includes(permission) || 
           allPermissions.includes('*:platform:*:*') ||
           (currentUser.value.role === 'admin' && permission.startsWith('*:platform:'))
  }
  
  // 检查平台访问权限
  const canAccessPlatform = (platformPath: string): boolean => {
    if (!currentUser.value) return false
    
    // 总平台内部页面始终可访问（已登录用户）
    if (platformPath.startsWith('/platform')) {
      return true
    }
    
    // 业务平台（只读模式）
    if (platformPath.includes('readonly=true')) {
      return true
    }
    
    // 内部平台需要相应权限
    const platformMap: Record<string, string> = {
      '/toolbox': '*:platform_toolbox:*:view',
      '/workflow-site': '*:platform_workflow:*:view',
      '/agent-site': '*:platform_agent:*:view',
      '/operation-site': '*:platform_operation:*:view'
    }
    
    const requiredPermission = platformMap[platformPath.split('?')[0]]
    if (requiredPermission) {
      return hasPermission(requiredPermission)
    }
    
    return false
  }
  
  // 初始化
  const initialize = () => {
    loadUserFromStorage()
  }
  
  return {
    // State
    currentUser,
    users,
    messages,
    auditLogs,
    
    // Getters
    isLoggedIn,
    currentUserPermissions,
    unreadMessageCount,
    highRiskOperationCount,
    
    // Actions
    setUser,
    loadUserFromStorage,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    markMessageAsRead,
    markAllMessagesAsRead,
    addAuditLog,
    hasPermission,
    canAccessPlatform,
    initialize
  }
})